import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import {
  PENDING_ACTION_ENQUIRE,
  PENDING_ACTION_FAVORITE,
  savePendingListingAction,
} from '../../util/pendingListingAction';
import {
  MODAL_SAVED,
  MODAL_SHARE,
  MODAL_SIGN_IN,
  SIGN_IN_INTENT_ENQUIRE,
  closeListingActionModal,
} from '../../ducks/favoriteListings.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { Modal } from '../../components';
import { CheckIcon, CloseIcon, HeartIcon, LinkIcon, LockIcon } from './ListingActionIcons';

import css from './ListingActionModals.module.css';

const ShareContent = ({ url, copied }) => (
  <>
    <div className={css.badge}>
      {copied ? <CheckIcon className={css.badgeIcon} /> : <LinkIcon className={css.badgeIcon} />}
    </div>
    <div className={css.title}>
      <FormattedMessage
        id={copied ? 'ListingActionModals.shareCopiedTitle' : 'ListingActionModals.shareManualTitle'}
      />
    </div>
    <div className={css.text}>
      <FormattedMessage
        id={copied ? 'ListingActionModals.shareCopiedText' : 'ListingActionModals.shareManualText'}
      />
    </div>
    {/* An input rather than text, so the link can be selected and copied by
        hand when the clipboard was not available. */}
    <label className={css.linkBox}>
      <LinkIcon className={css.linkIcon} />
      <input
        className={css.linkInput}
        type="text"
        readOnly
        value={url || ''}
        onFocus={e => e.target.select()}
      />
    </label>
  </>
);

const SavedContent = ({ onGoToFavourites, onDone }) => (
  <>
    <div className={css.badge}>
      <HeartIcon className={css.badgeIconGold} filled />
    </div>
    <div className={css.title}>
      <FormattedMessage id="ListingActionModals.savedTitle" />
    </div>
    <div className={css.text}>
      <FormattedMessage
        id="ListingActionModals.savedText"
        values={{
          favourites: (
            <strong className={css.strong}>
              <FormattedMessage id="ListingActionModals.favourites" />
            </strong>
          ),
        }}
      />
    </div>
    <div className={css.buttonRow}>
      <button type="button" className={css.secondaryButton} onClick={onGoToFavourites}>
        <FormattedMessage id="ListingActionModals.goToFavourites" />
      </button>
      <button type="button" className={css.primaryButton} onClick={onDone}>
        <FormattedMessage id="ListingActionModals.done" />
      </button>
    </div>
  </>
);

const SignInContent = ({ onSignIn, onApply }) => (
  <>
    <div className={css.badge}>
      <LockIcon className={css.badgeIcon} />
    </div>
    <div className={css.title}>
      <FormattedMessage id="ListingActionModals.signInTitle" />
    </div>
    <button type="button" className={css.primaryButtonFull} onClick={onSignIn}>
      <FormattedMessage id="ListingActionModals.signIn" />
    </button>
    <div className={css.memberSection}>
      <div className={css.memberPrompt}>
        <FormattedMessage id="ListingActionModals.notAMember" />
      </div>
      <button type="button" className={css.secondaryButtonFull} onClick={onApply}>
        <FormattedMessage id="ListingActionModals.applyForMembership" />
      </button>
    </div>
  </>
);

/**
 * The pop-ups behind the Share and Save buttons, and the sign-in prompt in
 * front of Save and Enquire for a signed-out visitor.
 *
 * Mounted once, by the Topbar, and driven by `modal` in the favoriteListings
 * duck. See that duck for why the buttons do not render their own.
 */
const ListingActionModals = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const modal = useSelector(state => state.favoriteListings.modal);

  const onClose = () => dispatch(closeListingActionModal());
  const onManageDisableScrolling = (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling));

  const type = modal?.type;
  const isEnquire = modal?.intent === SIGN_IN_INTENT_ENQUIRE;

  // Remember what they were doing, then send them to sign in and back here.
  // The listing is saved, or the application form opened, once they return.
  const onSignIn = () => {
    savePendingListingAction(
      isEnquire ? PENDING_ACTION_ENQUIRE : PENDING_ACTION_FAVORITE,
      modal?.listingId
    );
    onClose();
    const from = `${location.pathname}${location.search}${location.hash}`;
    history.push(pathByRouteName('LoginPage', routeConfiguration), { from });
  };

  // Not a member: the membership application, in the variant that matches why
  // they were asked to sign in.
  const onApply = () => {
    onClose();
    const variant = isEnquire ? 'inquiry' : 'apply';
    history.push(`${pathByRouteName('ContactPage', routeConfiguration)}?variant=${variant}`);
  };

  const onGoToFavourites = () => {
    onClose();
    history.push(pathByRouteName('FavoriteListingsPage', routeConfiguration));
  };

  const content =
    type === MODAL_SHARE ? (
      <ShareContent url={modal.url} copied={modal.copied} />
    ) : type === MODAL_SAVED ? (
      <SavedContent onGoToFavourites={onGoToFavourites} onDone={onClose} />
    ) : type === MODAL_SIGN_IN ? (
      <SignInContent onSignIn={onSignIn} onApply={onApply} />
    ) : null;

  return (
    <Modal
      id="ListingActionModals"
      isOpen={!!content}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      hideCloseBtn
      closeOnBackdropClick
      scrollLayerClassName={css.backdrop}
      containerClassName={css.dialog}
      contentClassName={css.content}
    >
      <button
        type="button"
        className={css.close}
        onClick={onClose}
        aria-label={intl.formatMessage({ id: 'ListingActionModals.close' })}
      >
        <CloseIcon className={css.closeIcon} />
      </button>
      {content}
      {type === MODAL_SHARE ? (
        <button type="button" className={css.primaryButtonFull} onClick={onClose}>
          <FormattedMessage id="ListingActionModals.done" />
        </button>
      ) : null}
    </Modal>
  );
};

export default ListingActionModals;
